<template>
  <div class="mb-1">
    <v-row class="mx-1">
      <v-dialog
        v-model="showSlideshow"
        :fullscreen="xs"
        width="600px"
        :height="xs ? '' : '700px'"
      >
        <global-card
          :title="t('images.title', 2)"
          closable
          @close="showSlideshow = false"
        >
          <image-slideshow
            v-if="readOnly"
            :id="`${name}-slide-show`"
            v-model="slideShowIndex"
            :images="slideShowImages"
            @close="showSlideshow = false"
          />
          <template #actions>
            <v-btn
              color="dark-grey"
              :text="t('actions.close')"
              @click="showSlideshow = false"
            />
          </template>
        </global-card>
      </v-dialog>
      <div
        v-for="(image, imageIndex) in images.filter((image) => !image.deleted)"
        :key="imageIndex"
      >
        <v-row class="mt-1">
          <v-col class="d-flex flex-column mx-2">
            <div
              class="image mb-2"
              :style="{
                border: getBorder(image),
                backgroundImage: `url(${getSizedHref(image, 'sm')})`,
                width: thumbnailWidth,
                height: thumbnailHeight
              }"
              @click.self="onImageClick(imageIndex, image)"
            >
              <div class="d-flex flex-row justify-space-between">
                <v-checkbox
                  v-if="selectable"
                  v-model="image.selected"
                  class="ma-2"
                />
                <v-btn
                  v-if="!readOnly"
                  v-model="image.selected"
                  icon="fas fa-trash"
                  color="error"
                  class="ma-2"
                  @click="image.deleted = true"
                />
              </div>
            </div>
            <div class="mb-6">
              <v-text-field
                v-if="!readOnly"
                v-model="image.title"
                :placeholder="t('global.title')"
                hide-details
              />
              <div
                v-if="readOnly"
                class="text-font"
                style="word-wrap:break-word; width: 300px"
              >
                {{ image.title }}
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-row>
    <image-upload-button
      v-if="!readOnly"
      class="mt-4"
      :caption="t('images.upload')"
      :on-upload-image="onUploadImage"
      :hint="hint"
      :on-upload-image-guard="onUploadImageGuard"
      :multiple-images="!isSingleImage"
      :error="error"
      :error-messages="errorMessages"
    />
    <v-alert
      v-if="(images.length === 0) && readOnly"
      class="text-font"
      :text="t('images.none_available')"
    />
  </div>
</template>

<script setup lang="ts">
import type { ImageValidationOutcome, ValidatableImage } from './useImageValidation';
import {
  computed,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import ImageSlideshow from './ImageSlideshow.vue';
import ImageUploadButton from './ImageUploadButton.vue';
import { useImageValidation } from './useImageValidation';

const props = withDefaults(defineProps<{
  name?: string;
  readOnly?: boolean;
  enableSlideShow?: boolean;
  selectable?: boolean;
  maxImages?: number;
  hint?: string;
  squareImage?: boolean;
  maxImageKilobyteSize?: number;
  minImageDimSize?: number;
  minWidthImageDimSize?: number;
  minHeightImageDimSize?: number;
  errorMessages?: any;
  error?: boolean;
}>(), {
  maxImages: () => 0,
  squareImage: () => false,
  minImageDimSize: () => 0,
  minWidthImageDimSize: () => 0,
  minHeightImageDimSize: () => 0,
});
const { t } = useI18n();
const { validateImage } = useImageValidation();

const { xs } = useDisplay();

/** A gallery image: a stored one, or one just uploaded and not yet saved. */
type GalleryImage = App.Models.Image & {
  base_64_image?: string;
  selected?: boolean;
  deleted?: boolean;
};

const images = defineModel<GalleryImage[]>({ default: () => [] });
const slideShowIndex = ref<number | null>(null);
const showSlideshow = ref(false);
const thumbnailWidth = '300px';

const thumbnailHeight = computed(() => {
  if (props.squareImage) {
    return '300px';
  }
  return '200px';
});

/**
 * Resolve the best URL for a given image and size hint.
 * Falls back gracefully through md → full when a specific variant is absent.
 */
function getSizedHref(image: any, size: 'full' | 'md' | 'sm' = 'full'): string {
  if (image.base_64_image) {
    return image.base_64_image;
  }
  const urls = image.signed_urls as Record<string, string> | undefined;
  if (urls) {
    return urls[size] ?? urls.md ?? urls.full ?? image.signed_file_url ?? '';
  }
  return image.signed_file_url ?? '';
}

const slideShowImages = computed(() => images.value.filter((image) => !image.deleted).map((image) => ({
  title: image.title,
  // Use the medium variant in the carousel (good quality, faster load)
  href: getSizedHref(image, 'md'),
})));

const isSingleImage = computed(() => props.maxImages === 1);

function onImageClick(imageIndex: number, image: GalleryImage): void {
  if (props.enableSlideShow) {
    slideShowIndex.value = imageIndex;
    showSlideshow.value = true;
  }
  else if (props.selectable) {
    image.selected = !image.selected;
  }
}

function getBorder(image: GalleryImage): string {
  if (props.selectable && image.selected) {
    return '2px solid #02979d';
  }
  return '';
}

function onUploadImage(uploadedImages: UploadedImage[]): void {
  let lowestId = -1;
  if (images.value.length > 0) {
    lowestId = Math.min.apply(null, images.value.map((image) => image.id));
    lowestId = lowestId > -1 ? -1 : lowestId - 1;
  }
  uploadedImages.forEach((newImage) => {
    images.value.push({
      id: lowestId,
      folder: null,
      title: newImage.title,
      hash: null,
      status: 'pending',
      created_at: null,
      created_by: null,
      updated_at: null,
      updated_by: null,
      base_64_image: newImage.data,
      selected: false,
      deleted: false,
    });
    lowestId -= 1;
  });
  if (props.maxImages > 0) {
    let oldest = images.value.find((image) => !image.deleted);
    while (oldest && images.value.filter((image) => !image.deleted).length > props.maxImages) {
      oldest.deleted = true;
      oldest = images.value.find((image) => !image.deleted);
    }
  }
}
function onUploadImageGuard(image: ValidatableImage): ImageValidationOutcome {
  return validateImage(
    image,
    props.squareImage,
    props.maxImageKilobyteSize,
    props.minImageDimSize,
    props.minWidthImageDimSize,
    props.minHeightImageDimSize,
  );
}
</script>

<style scoped>
.image {
  float: left;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  border: 1px solid #ebebeb;
}
</style>
